/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {Store} from "@ngrx/store";
import {catchError, concatMap, exhaustMap, map, of, switchMap} from "rxjs";
import {tap} from "rxjs/operators";
import {concatLatestFrom} from "@ngrx/operators";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ErrorHandlingUtil} from "../../utils/error-handling-util";
import {PatternGroupService} from "../../services/pattern-group/pattern-group.service";
import {PatternGroupActions} from "./pattern-group.actions";
import {selectGroupFilterQuery} from "./pattern-group.selectors";

export const loadGroupList = createEffect(
    (actions$ = inject(Actions), groupService = inject(PatternGroupService)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.loadAll),
            switchMap((action) =>
                groupService.getGroups(action.filterQuery).pipe(
                    map((groups) => PatternGroupActions.loadAllSuccess({groups})),
                    catchError((error: any) => of(PatternGroupActions.loadAllError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadGroup = createEffect(
    (actions$ = inject(Actions), groupService = inject(PatternGroupService)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.load),
            switchMap((action) =>
                groupService.getGroup(action.groupId).pipe(
                    map((group) => PatternGroupActions.loadSuccess({group})),
                    catchError((error: any) => of(PatternGroupActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)})))
                )
            )
        );
    },
    {functional: true}
);

export const saveGroup = createEffect(
    (actions$ = inject(Actions), groupService = inject(PatternGroupService)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.save),
            concatMap((action) => {
                const group = action.group;
                const save$ = group.id
                    ? groupService.updateGroup(group.id, group)
                    : groupService.createGroup(group);
                return save$.pipe(
                    map((saved) => PatternGroupActions.saveSuccess({group: saved})),
                    catchError((error: any) => of(PatternGroupActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)})))
                );
            })
        );
    },
    {functional: true}
);

export const showSaveSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.saveSuccess),
            tap(() => {
                snackBar.open("Group saved successfully.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const copyGroup = createEffect(
    (actions$ = inject(Actions), groupService = inject(PatternGroupService)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.copy),
            exhaustMap((action) =>
                groupService.copyGroup(action.groupId, action.newName).pipe(
                    map((group) => PatternGroupActions.copySuccess({group})),
                    catchError((error: any) => of(PatternGroupActions.copyError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)})))
                )
            )
        );
    },
    {functional: true}
);

export const showCopySuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.copySuccess),
            tap(() => {
                snackBar.open("Group copied successfully.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const setDefaultGroup = createEffect(
    (actions$ = inject(Actions), groupService = inject(PatternGroupService)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.setDefault),
            exhaustMap((action) =>
                groupService.setDefault(action.groupId).pipe(
                    map((group) => PatternGroupActions.setDefaultSuccess({group})),
                    catchError((error: any) => of(PatternGroupActions.setDefaultError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)})))
                )
            )
        );
    },
    {functional: true}
);

export const toggleFavourite = createEffect(
    (actions$ = inject(Actions), groupService = inject(PatternGroupService)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.toggleFavourite),
            exhaustMap((action) =>
                groupService.toggleFavourite(action.groupId).pipe(
                    map((group) => PatternGroupActions.toggleFavouriteSuccess({group})),
                    catchError((error: any) => of(PatternGroupActions.toggleFavouriteError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)})))
                )
            )
        );
    },
    {functional: true}
);

export const toggleArchive = createEffect(
    (actions$ = inject(Actions), groupService = inject(PatternGroupService)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.toggleArchive),
            exhaustMap((action) =>
                groupService.toggleArchive(action.groupId).pipe(
                    map((group) => PatternGroupActions.toggleArchiveSuccess({group})),
                    catchError((error: any) => of(PatternGroupActions.toggleArchiveError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)})))
                )
            )
        );
    },
    {functional: true}
);

export const reloadGroupsAfterMutation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                PatternGroupActions.saveSuccess,
                PatternGroupActions.copySuccess,
                PatternGroupActions.setDefaultSuccess,
                PatternGroupActions.toggleFavouriteSuccess,
                PatternGroupActions.toggleArchiveSuccess,
            ),
            concatLatestFrom(() => store$.select(selectGroupFilterQuery)),
            map(([, filterQuery]) => PatternGroupActions.loadAll({filterQuery}))
        );
    },
    {functional: true}
);

export const reloadGroupsOnFilterQueryUpdate = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(PatternGroupActions.updateFilterQuery),
            map((action) => PatternGroupActions.loadAll({filterQuery: action.filterQuery}))
        );
    },
    {functional: true}
);
